import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Company {
  _id: string;
  name: string;
}

const CompanyDetails: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [search, setSearch] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companySelected, setCompanySelected] = useState(false); // Flag for company selection
  const [showModal, setShowModal] = useState(false); // Modal state
  const [newCompany, setNewCompany] = useState({
    name: "",
    logo: "",
    industry: "",
    country: "",
    teamSize: "",
    fundingAmount: "",
    inceptionYear: "",
    website: "",
    linkedin: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch("https://d2q95isnao7wof.cloudfront.net/api/companies/getallcompanies");
        const data = await response.json();
        setCompanies(data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!companySelected && search) {
      const filtered = companies.filter((company) =>
        company.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies([]);
    }
  }, [search, companies, companySelected]);

  const handleSignUp = () => {
    if (selectedCompany) {
      navigate(`/signup?companyId=${selectedCompany}`);
    }
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company._id); // Set the selected company ID
    setSearch(company.name); // Update the search input with the company name
    setFilteredCompanies([]); // Close the dropdown
    setCompanySelected(true); // Mark the company as selected
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCompanySelected(false); // Reset the selection flag if user starts typing
  };

  const handleAddCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch("https://d2q95isnao7wof.cloudfront.net/api/companies/createcompany", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newCompany),
      });
  
      if (response.ok) {
        const data = await response.json(); // Parse the JSON response
        alert("Company added successfully!");
        setShowModal(false); // Close the modal
  
        // Redirect to the signup page with the new company details (companyId as query parameter)
        navigate(`/signup?companyId=${data._id}`); // Assuming the response contains the new company's _id
      } else {
        alert("Failed to add company.");
      }
    } catch (error) {
      console.error("Error adding company:", error);
      alert("An error occurred. Please try again.");
    }
  };
  
  

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <header className="text-center text-3xl font-extrabold text-yellow-600 mb-8">
        Select Your Company
      </header>

      <div className="flex justify-center mb-6 relative">
        <input
          type="text"
          placeholder="Search for your company..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="p-3 border rounded w-full max-w-md"
        />
        {!companySelected && search && (
          <div className="absolute top-full mt-1 w-full max-w-md bg-white border rounded shadow-md z-10">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <div
                  key={company._id}
                  className="p-3 hover:bg-gray-200 cursor-pointer"
                  onClick={() => handleCompanySelect(company)} // Close dropdown on selection
                >
                  {company.name}
                </div>
              ))
            ) : (
              <div className="p-3 text-gray-500 text-center">No match found</div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-center mb-4">
        <button
          onClick={handleSignUp}
          className="bg-blue-600 text-white px-6 py-3 rounded shadow hover:bg-blue-700 transition"
          disabled={!selectedCompany}
        >
          Sign Up
        </button>
      </div>

      <div className="flex justify-center">
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-6 py-3 rounded shadow hover:bg-green-700 transition"
        >
          Add New Company
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <form
            onSubmit={handleAddCompany}
            className="bg-white p-6 rounded shadow-md space-y-4 w-96"
          >
            <h2 className="text-xl font-bold text-center text-yellow-600">
              Add New Company
            </h2>

            <input
              type="text"
              placeholder="Company Name"
              value={newCompany.name}
              onChange={(e) =>
                setNewCompany({ ...newCompany, name: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            />

            <input
              type="url"
              placeholder="Logo URL"
              value={newCompany.logo}
              onChange={(e) =>
                setNewCompany({ ...newCompany, logo: e.target.value })
              }
              className="w-full p-3 border rounded"
            />

            <input
              type="text"
              placeholder="Industry"
              value={newCompany.industry}
              onChange={(e) =>
                setNewCompany({ ...newCompany, industry: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            />

            <input
              type="text"
              placeholder="Country"
              value={newCompany.country}
              onChange={(e) =>
                setNewCompany({ ...newCompany, country: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            />

            <select
              value={newCompany.teamSize}
              onChange={(e) =>
                setNewCompany({ ...newCompany, teamSize: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            >
              <option value="">Select Team Size</option>
              <option value="1-10">1-10</option>
              <option value="11-50">11-50</option>
              <option value="51-100">51-100</option>
            </select>

            <input
              type="number"
              placeholder="Funding Amount (in USD)"
              value={newCompany.fundingAmount}
              onChange={(e) =>
                setNewCompany({ ...newCompany, fundingAmount: e.target.value })
              }
              className="w-full p-3 border rounded"
            />

            <input
              type="number"
              placeholder="Inception Year"
              value={newCompany.inceptionYear}
              onChange={(e) =>
                setNewCompany({ ...newCompany, inceptionYear: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            />

            <input
              type="url"
              placeholder="Website URL"
              value={newCompany.website}
              onChange={(e) =>
                setNewCompany({ ...newCompany, website: e.target.value })
              }
              required
              className="w-full p-3 border rounded"
            />

            <input
              type="url"
              placeholder="LinkedIn URL"
              value={newCompany.linkedin}
              onChange={(e) =>
                setNewCompany({ ...newCompany, linkedin: e.target.value })
              }
              className="w-full p-3 border rounded"
            />

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Add Company
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CompanyDetails;
